import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import type { OrganizerPhoto } from "@ui/components/PhotoOrganizer";
import { SoftButton } from "@ui/components/SoftButton";
import { EditorSection } from "@/components/ListingFields/EditorSectionHeader";
import { PhotosField, photosFieldCopy } from "@/components/ListingFields/PhotosField";
import type { UploadFileItem } from "@/components/PhotoUploadModal/PhotoUploadModal";
import { firstError } from "@/lib/forms/first-error";

const LISTING_TYPE = "product" as const;

export interface PhotoManagerController {
  closeModal: () => void;
  confirmUpload: () => void;
  isModalOpen: boolean;
  openModal: () => void;
  photos: OrganizerPhoto[];
  removePhoto: (photoId: string) => void;
  reorderPhotos: (orderedIds: string[]) => void;
  upload: {
    addFiles: (files: FileList | File[]) => void;
    allCompleted: boolean;
    files: UploadFileItem[];
    isUploading: boolean;
    removeFile: (id: string) => void;
  };
}

export interface ProductPhotosEditorProps {
  errors?: Record<string, string[]>;
  manager: PhotoManagerController;
  minPhotos: number;
}

export function ProductPhotosEditor({ errors, manager, minPhotos }: ProductPhotosEditorProps) {
  const hasPhotos = manager.photos.length > 0;
  const copy = photosFieldCopy({ hasPhotos, listingType: LISTING_TYPE, minPhotos });

  return (
    <EditorSection
      {...copy}
      headerAction={
        hasPhotos ? (
          <SoftButton
            aria-label={t("web.listings.fields.photos.add_more")}
            className="lg:size-10"
            onPress={manager.openModal}
          >
            <Icon name="add" size="20" />
          </SoftButton>
        ) : undefined
      }
    >
      <PhotosField
        allUploadsCompleted={manager.upload.allCompleted}
        error={firstError(errors?.photos)}
        isUploadModalOpen={manager.isModalOpen}
        isUploading={manager.upload.isUploading}
        listingType={LISTING_TYPE}
        minPhotos={minPhotos}
        onAddFiles={manager.upload.addFiles}
        onAddPhotos={manager.openModal}
        onConfirmUpload={manager.confirmUpload}
        onDeletePhoto={manager.removePhoto}
        onRemoveUploadFile={manager.upload.removeFile}
        onReorderPhotos={manager.reorderPhotos}
        onUploadModalOpenChange={(open) => {
          if (!open) manager.closeModal();
        }}
        photos={manager.photos}
        uploadFiles={manager.upload.files}
      />
    </EditorSection>
  );
}
